import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Delete } from 'lucide-react';

interface VirtualKeyboardProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  className?: string;
}

const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  value,
  onChange,
  onClear,
  className = ''
}) => {
  // Keyboard is always shown, no state needed

  const numberKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
  const operatorKeys = ['+', '-', '×', '÷', '=', '.', '(', ')', '%'];
  const specialKeys = ['π', 'e', '√', '^', '∞', '±', '°', '∠', '∆'];

  const handleKeyPress = (key: string) => {
    let newValue = value;

    switch (key) {
      case 'BACKSPACE':
        newValue = value.slice(0, -1);
        break;
      case 'CLEAR':
        newValue = '';
        onClear();
        return;
      case '×':
        newValue = value + '*';
        break;
      case '÷':
        newValue = value + '/';
        break;
      case '√':
        newValue = value + 'sqrt(';
        break;
      case '^':
        newValue = value + '^';
        break;
      case 'π':
        newValue = value + 'π';
        break;
      default:
        newValue = value + key;
    }

    onChange(newValue);
  };

  // No toggle function needed since keyboard is always shown

  return (
    <div className={`w-full ${className}`}>
      {/* Answer Display */}
      <div className="mb-3">
        <div className="w-full px-4 py-3 text-lg border-2 border-blue-300 rounded-lg bg-blue-50 min-h-[50px] flex items-center">
          <span className="text-gray-700 font-medium">
            {value || <span className="text-gray-400 italic">Use the virtual keyboard below to enter your answer</span>}
          </span>
        </div>
      </div>

      {/* Virtual Keyboard - Always Shown */}
      <div className="w-full">
        <Card className="mt-3 p-4 bg-gray-50 border-2 border-blue-200">
          <div className="space-y-3">
            {/* Numbers Row */}
            <div className="grid grid-cols-5 gap-2">
              {numberKeys.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => handleKeyPress(key)}
                  className="h-12 text-xl font-semibold hover:bg-blue-100"
                >
                  {key}
                </Button>
              ))}
            </div>

            {/* Operators Row */}
            <div className="grid grid-cols-5 gap-2">
              {operatorKeys.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => handleKeyPress(key)}
                  className="h-12 text-xl font-semibold hover:bg-green-100 text-green-700"
                >
                  {key}
                </Button>
              ))}
            </div>

            {/* Special Characters Row */}
            <div className="grid grid-cols-5 gap-2">
              {specialKeys.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => handleKeyPress(key)}
                  className="h-12 text-lg font-semibold hover:bg-purple-100 text-purple-700"
                >
                  {key}
                </Button>
              ))}
            </div>

            {/* Action Buttons Row */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleKeyPress('BACKSPACE')}
                className="h-12 hover:bg-red-100 text-red-600"
              >
                <Delete className="h-4 w-4 mr-2" />
                Backspace
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleKeyPress('CLEAR')}
                className="h-12 hover:bg-red-100 text-red-600"
              >
                Clear All
              </Button>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-3 text-xs text-gray-600 text-center">
            <p><strong>Note:</strong> Use the virtual keypad for numeric input to ensure accuracy</p>
            <p>Click the buttons above to enter your numerical answer</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default VirtualKeyboard;
